import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Parse from "html-react-parser";
import { useSelector } from "react-redux";
import { Column } from "components/Layout/Grid";
import ManagementPanelModalCard from "components/Modal/ManagementPanelModalCard";
import Table from "components/Table";
import toast from "components/Toast/dispatch";
import {
  statusLockedButton,
  statusProcessing,
  statusSent,
  supplierStatusLockedButton,
  supplierStatusSent,
} from "components/TablePortal/interface";
import { supplierStatusValues } from "components/Table/interface";
import { RootState } from "store/ducks";
import ManagementPanelService from "services/managementPanel.service";
import QuoteServices from "services/quote.service";
import { toastNonstandardCode } from "pages/Quotes/Dashboard/utils";
import ErrorIcon from "img/error-img.svg";
import { unansweredTableModel } from "./unansweredTableModel";
import { UnansweredItemsDetailDropdown, type IUnansweredRow } from "./UnansweredItemsDetailDropdown";

function mapUnansweredApiItemToRow(item: any): IUnansweredRow {
  const customerCode = item.buyer?.identifier ?? item.company?.crmCustomerCode ?? item.crmCustomerCode;
  return {
    id: item.id ?? item.quotationId ?? item.quotation_id,
    code: item.code ?? item.codigo ?? item.quotationCode,
    createdAt: item.createdAt ?? item.dataCriacao,
    customerName: item.company?.crmCustomerCode,
    razaoSocial: item.buyer?.razaoSocial ?? item.buyer?.companyName ?? item.razaoSocial ?? item.buyer?.name,
    category: item.categoryMafra,
    qtd: item.itemsCount,
    status: item.status,
    statusDescription: item.statusDescription ?? item.descricaoStatus,
    supplierStatus: item.supplierStatus ?? item.supplierStatusDescription,
    expiresAt: item.expiresAt,
    origin: item.origin ?? item.origem,
    items: item.items ?? item.itens ?? [],
    company: item.company ? { crmCustomerCode: item.company.crmCustomerCode ?? customerCode } : { crmCustomerCode: customerCode },
  };
}

const PAGE_SIZE = 10;

const MOCK_UNANSWERED_ITEMS_RESPONSE = {
  data: [],
  total: 0,
};

export interface ModalTotalUnansweredItemsProps {
  open: boolean;
  onClose: () => void;
  filterCurrent: Record<string, any>;
}

export const ModalTotalUnansweredItems: React.FC<ModalTotalUnansweredItemsProps> = ({
  open,
  onClose,
  filterCurrent,
}) => {
  const user = useSelector((state: RootState) => state.user);
  const quoteItemStorage = useSelector((state: RootState) => state.quoteItem) ?? { id: "" };
  const blockedSimultaneousAccess = process.env.REACT_APP_BLOCKED_SIMULTANEOUS_ACCESS;
  const bcgOnly = process.env.REACT_APP_BCG_ONLY === "true";

  const _managementPanelService = new ManagementPanelService();
  const _quotesServices = new QuoteServices();

  const searchValueRef = useRef("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [unansweredTableData, setUnansweredTableData] = useState<IUnansweredRow[]>([]);
  const [totalUnansweredCount, setTotalUnansweredCount] = useState(0);
  const [loadingUnansweredTable, setLoadingUnansweredTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [tableOrder, setTableOrder] = useState("");

  const conditionalStatusSent = useCallback((status: string, supplierStatus: string) => {
    return statusSent.includes(status) && supplierStatusSent.includes(supplierStatus);
  }, []);

  const handleOpenQuotation = useCallback(
    async (row: IUnansweredRow) => {
      if (!row.id) return;

      const code = row.code ?? "";
      if (code.length > 9) {
        toastNonstandardCode(row.origin ?? "");
        return;
      }

      if (!row.company?.crmCustomerCode) {
        toast.info(
          "Cliente não cadastrado",
          "Para continuar com a cotação, é necessário cadastrar o cliente antes."
        );
        return;
      }

      const isDisconsidered = row.supplierStatus === "DISREGARDED";
      if (isDisconsidered && (bcgOnly || user?.isBCGUser)) {
        toast.info("Cotação desconsiderada", "A cotação foi desconsiderada por condições comerciais.");
        return;
      }

      if (statusProcessing.includes(row.status ?? "")) {
        return;
      }

      const verifyStatusQuotation =
        (statusLockedButton.includes(row.status ?? "") &&
          supplierStatusLockedButton.includes(row.supplierStatus ?? "")) ||
        (statusSent.includes(row.status ?? "") &&
          supplierStatusSent.includes(row.supplierStatus ?? ""));

      const openInNewTab = (path: string) => {
        window.open(path, "_blank", "noopener,noreferrer");
      };

      if (verifyStatusQuotation) {
        try {
          const response = await _quotesServices.checkQuotationViewed(row.id);
          const users: string[] = response?.data?.data?.user ?? [];

          if (
            blockedSimultaneousAccess &&
            users?.length > 0 &&
            !users.includes(user?.fullName ?? "") &&
            quoteItemStorage
          ) {
            const description = Parse(
              `<p class='text-styles color-blue-gray mt-3 text-wrap text-center description-quote-blocked'>Essa cotação está sendo manipulada pelo colaborador <b>${users[0]}</b>. Aguarde a finalização para acessar as informações.</p>`
            );
            toast.errorCustom(
              "Importante!",
              description,
              ErrorIcon,
              true,
              "Fechar",
              19,
              14,
              true
            );
            return;
          }

          if (statusSent.includes(row.status ?? "") && supplierStatusSent.includes(row.supplierStatus ?? "")) {
            openInNewTab(`/gravado-no-money/${row.id}`);
          } else {
            openInNewTab(`/cotacoes-nova/${row.id}`);
          }
        } catch {
          openInNewTab(`/cotacoes-nova/${row.id}`);
        }
      } else {
        if (conditionalStatusSent(row.status ?? "", row.supplierStatus ?? "")) {
          if (supplierStatusValues.includes(row.supplierStatus ?? "")) {
            openInNewTab(`/gravado-no-money/${row.id}`);
          } else {
            toast.info("Atenção", "Não é possível editar a cotação escolhida.");
          }
        } else {
          openInNewTab(`/cotacoes-nova/${row.id}`);
        }
      }
    },
    [conditionalStatusSent, user, quoteItemStorage, blockedSimultaneousAccess, bcgOnly]
  );

  const unansweredTableModelForTable = useMemo(
    () => unansweredTableModel(handleOpenQuotation).filter((m) => m.key !== "blockedQuote"),
    [handleOpenQuotation]
  );

  const totalPages = Math.ceil(totalUnansweredCount / PAGE_SIZE) || 1;

  async function loadTotalUnansweredItems(page: number = 0, userEmail: string = selectedUserEmail) {
    setLoadingUnansweredTable(true);
    try {
      const searchTerm = searchValueRef.current.trim();
      const trackedUser = userEmail.trim();
      const filterMapping = {
        ProductCategories: filterCurrent.ProductCategories,
        SellerCodes: filterCurrent.SellerCodes,
        Origin: filterCurrent.Origin,
        BusinessUnitIdentifier: filterCurrent.BusinessUnitIdentifier,
        StartDate: filterCurrent.StartDate,
        EndDate: filterCurrent.EndDate,
        Top: PAGE_SIZE,
        Skip: page * PAGE_SIZE,
        ...(searchTerm ? {
          Code: searchTerm,
          Description: searchTerm,
          BuyerName: searchTerm,
          CompanyCrmCustomerCode: searchTerm,
          Name: searchTerm,
        } : {}),
        ...(trackedUser ? { TrackedUser: trackedUser } : {}),
      };

      let response: any;
      try {
        response = await _managementPanelService.getQuotationsByUnansweredItems(filterMapping);
      } catch {
        response = MOCK_UNANSWERED_ITEMS_RESPONSE;
      }

      const rawData = response?.data ?? response ?? [];
      const total = response?.total ?? (Array.isArray(rawData) ? rawData.length : 0);
      const rows: IUnansweredRow[] = Array.isArray(rawData)
        ? rawData.map((item: any) => mapUnansweredApiItemToRow(item))
        : [];

      setUnansweredTableData(rows);
      setTotalUnansweredCount(typeof total === "number" ? total : rows.length);
      setCurrentPage(page);
    } catch {
      setUnansweredTableData([]);
      setTotalUnansweredCount(0);
    } finally {
      setLoadingUnansweredTable(false);
    }
  }

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  function handleKeyDown(key: string) {
    if (key === "Enter") {
      setCurrentPage(0);
      loadTotalUnansweredItems(0);
    }
  }

  function handlePageChange(newPage: number) {
    setCurrentPage(newPage);
    loadTotalUnansweredItems(newPage);
  }

  if (!open) return null;

  return (
    <ManagementPanelModalCard
      title="Itens não respondidos"
      searchFieldLabel="Buscar ID, razão social, cód. do cliente, descrição do produto"
      isLoading={loadingUnansweredTable}
      onSearchSubmit={(value) => {
        searchValueRef.current = value;
      }}
      onCloseAction={onClose}
      onKeyDown={(e) => handleKeyDown(e)}
      onOpen={() => {
        searchValueRef.current = "";
        setSelectedUserEmail("");
        setCurrentPage(0);
        loadTotalUnansweredItems(0, "");
      }}
      onUserSelect={(selectedUser) => {
        setSelectedUserEmail(selectedUser.email);
        setCurrentPage(0);
        loadTotalUnansweredItems(0, selectedUser.email);
      }}
      onUserClear={() => {
        setSelectedUserEmail("");
        setCurrentPage(0);
        loadTotalUnansweredItems(0, "");
      }}
    >
      <Column className="w-100 w-lg-100 px-1 pl-sm-3 pl-md-3 pl-lg-3 mt-4">
        <Table
          trackingKey="Itens não respondidos - Table"
          model={unansweredTableModelForTable}
          data={unansweredTableData}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          setTableOrder={setTableOrder}
          tableOrder={tableOrder}
          showPagination={true}
          DropdownTemplate={UnansweredItemsDetailDropdown}
          portalCustomHeader
          className="w-100 unanswered-items-table unanswered-items-table--table"
          emptyDataMessage="Nenhum item não respondido encontrado"
        />
      </Column>
    </ManagementPanelModalCard>
  );
};
